import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Typography, Button, Paper, Table, TableHead, TableBody,
    TableRow, TableCell, Chip, IconButton, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, MenuItem, CircularProgress,
    Alert, FormControl, InputLabel, Select, OutlinedInput,
    Checkbox, ListItemText, Tooltip, ListSubheader, InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PublishIcon from '@mui/icons-material/Publish';
import BlockIcon from '@mui/icons-material/Block';
import { toast } from 'react-toastify';
import { directorAPI, publicAPI } from '../../services/api';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import useAuth from '../../hooks/useAuth';

const LEVELS = [
    'artisan', 'qualifiant', 'technicien',
    'bac', 'bac+1', 'bac+2', 'bac+3', 'bac+4',
    'bac+5', 'bac+6', 'bac+7', 'bac+8',
];

const statusColors = { brouillon: 'default', publiee: 'success', fermee: 'error' };
const statusLabels = { brouillon: 'Brouillon', publiee: 'Publiée', fermee: 'Fermée' };

export default function DirectorOffers() {
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin';

    const [offers, setOffers] = useState([]);
    const [filieres, setFilieres] = useState([]);
    const [istas, setIstas] = useState([]);
    const [filiereModules, setFiliereModules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialog, setDialog] = useState(false);
    const [editing, setEditing] = useState(null);
    const [deleteId, setDeleteId] = useState(null);
    const [error, setError] = useState('');
    const [filiereSearch, setFiliereSearch] = useState('');

    const [form, setForm] = useState({
        filiere_ids: [], title: '', description: '',
        required_levels: [], module_ids: [], types: [],
        ista_id: '',
    });

    useEffect(() => { loadData(); }, []);

    const loadData = async () => {
        try {
            const [offRes, filRes, istaRes] = await Promise.allSettled([
                directorAPI.getJobOffers(),
                directorAPI.getFilieres(),
                publicAPI.getIstas(),
            ]);
            if (offRes.status === 'fulfilled') setOffers(offRes.value.data);
            else console.error('Error loading offers:', offRes.reason);
            if (filRes.status === 'fulfilled') setFilieres(filRes.value.data);
            else console.error('Error loading filieres:', filRes.reason);
            if (istaRes.status === 'fulfilled') setIstas(istaRes.value.data);
            else console.error('Error loading istas:', istaRes.reason);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    // Load modules when filiere changes
    useEffect(() => {
        if (form.filiere_ids && form.filiere_ids.length > 0) {
            Promise.all(form.filiere_ids.map(id => publicAPI.getFiliereModules(id)))
                .then(responses => {
                    const allModules = responses.flatMap(res => res.data);
                    const uniqueModules = Array.from(new Map(allModules.map(m => [m.id, m])).values());
                    setFiliereModules(uniqueModules);
                })
                .catch(() => setFiliereModules([]));
        } else {
            setFiliereModules([]);
        }
    }, [form.filiere_ids]);

    // Auto-calc masse horaire from selected modules
    const totalHours = useMemo(() => {
        return filiereModules
            .filter(m => form.module_ids.includes(m.id))
            .reduce((sum, m) => sum + (m.masse_horaire || 0), 0);
    }, [form.module_ids, filiereModules]);

    // Filter filieres by search term (name or code)
    const filteredFilieres = useMemo(() => {
        const q = filiereSearch.trim().toLowerCase();
        if (!q) return filieres;
        return filieres.filter(f =>
            `${f.nom} ${f.code}`.toLowerCase().includes(q)
        );
    }, [filieres, filiereSearch]);

    const handleOpen = (offer = null) => {
        if (offer) {
            setEditing(offer.id);
            setForm({
                filiere_ids: offer.filieres?.map(f => f.id) || [], title: offer.title,
                description: offer.description || '', required_levels: offer.required_levels || [],
                module_ids: offer.modules?.map(m => m.id) || [], types: offer.types || [],
                ista_id: offer.ista_id || '',
            });
        } else {
            setEditing(null);
            setForm({ filiere_ids: [], title: '', description: '', required_levels: [], module_ids: [], types: [], ista_id: user?.ista_id || '' });
        }
        setError('');
        setFiliereSearch('');
        setDialog(true);
    };

    const handleSave = async () => {
        setError('');
        try {
            if (editing) {
                await directorAPI.updateJobOffer(editing, form);
                toast.success('Offre modifiée');
            } else {
                await directorAPI.createJobOffer(form);
                toast.success('Offre créée (brouillon)');
            }
            setDialog(false);
            loadData();
        } catch (err) {
            setError(err.response?.data?.message || 'Erreur');
        }
    };

    const handlePublish = async (id) => {
        try {
            await directorAPI.publishJobOffer(id);
            toast.success('Offre publiée !');
            loadData();
        } catch (err) { toast.error('Erreur'); }
    };

    const handleClose = async (id) => {
        try {
            await directorAPI.closeJobOffer(id);
            toast.success('Offre fermée');
            loadData();
        } catch (err) { toast.error('Erreur'); }
    };

    const handleDelete = async () => {
        try {
            await directorAPI.deleteJobOffer(deleteId);
            toast.success('Offre supprimée');
            setDeleteId(null);
            loadData();
        } catch (err) { toast.error('Erreur'); }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h5" fontWeight={700}>Gérer les offres</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}
                    sx={{ background: 'linear-gradient(135deg, #1a5276, #2e86c1)', textTransform: 'none', borderRadius: 2 }}>
                    Nouvelle offre
                </Button>
            </Box>

            <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
                <div className="responsive-table-wrapper">
                    <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f7fa' }}>
                            <TableCell sx={{ fontWeight: 600 }}>Titre</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Filière</TableCell>
                            {isAdmin && <TableCell sx={{ fontWeight: 600 }}>ISTA</TableCell>}
                            <TableCell sx={{ fontWeight: 600 }}>Niveau</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Masse H.</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Statut</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Candidatures</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {offers.map(o => (
                            <TableRow key={o.id} hover>
                                <TableCell>
                                    <Typography variant="body2" fontWeight={600}>{o.title}</Typography>
                                </TableCell>
                                <TableCell>
                                    {o.filieres?.map(f => (
                                        <Chip key={f.id} label={f.code} size="small" variant="outlined" sx={{ mr: 0.5, mb: 0.5 }} />
                                    ))}
                                </TableCell>
                                {isAdmin && (
                                    <TableCell>
                                        {o.ista ? (
                                            <Chip label={o.ista.code} size="small" color="info" variant="outlined" />
                                        ) : '—'}
                                    </TableCell>
                                )}
                                <TableCell>
                                    {o.required_levels?.map(l => (
                                        <Chip key={l} label={l} size="small" variant="outlined" sx={{ mr: 0.5, mb: 0.5 }} />
                                    ))}
                                </TableCell>
                                <TableCell>
                                    {o.types?.map(t => (
                                        <Chip key={t} label={t} size="small" color="secondary" variant="outlined" sx={{ mr: 0.5, mb: 0.5 }} />
                                    ))}
                                </TableCell>
                                <TableCell>{o.total_hours}h</TableCell>
                                <TableCell>
                                    <Chip label={statusLabels[o.status]} color={statusColors[o.status]} size="small" />
                                </TableCell>
                                <TableCell>
                                    <Chip label={o.candidatures_count || 0} size="small" color="primary" variant="outlined" />
                                </TableCell>
                                <TableCell>
                                    {o.status === 'brouillon' && (
                                        <Tooltip title="Publier">
                                            <IconButton size="small" color="success" onClick={() => handlePublish(o.id)}>
                                                <PublishIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {o.status === 'publiee' && (
                                        <Tooltip title="Fermer">
                                            <IconButton size="small" color="warning" onClick={() => handleClose(o.id)}>
                                                <BlockIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    <Tooltip title="Modifier">
                                        <IconButton size="small" color="primary" onClick={() => handleOpen(o)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Supprimer">
                                        <IconButton size="small" color="error" onClick={() => setDeleteId(o.id)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                    </Table>
                </div>
            </Paper>

            {/* ── Create / Edit Dialog ────────────────────── */}
            <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editing ? 'Modifier l\'offre' : 'Nouvelle offre (brouillon)'}</DialogTitle>
                <DialogContent>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                    <TextField fullWidth label="Titre" value={form.title}
                        onChange={(e) => setForm({ ...form, title: e.target.value })} sx={{ mt: 1, mb: 2 }} />

                    {isAdmin && (
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>ISTA (Établissement)</InputLabel>
                            <Select
                                value={form.ista_id}
                                onChange={(e) => setForm({ ...form, ista_id: e.target.value })}
                                label="ISTA (Établissement)"
                            >
                                <MenuItem value=""><em>Par défaut</em></MenuItem>
                                {istas.map(i => (
                                    <MenuItem key={i.id} value={i.id}>{i.nom} ({i.code})</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Filières</InputLabel>
                        <Select
                            multiple
                            value={form.filiere_ids}
                            onChange={(e) => {
                                const val = e.target.value;
                                setForm({ ...form, filiere_ids: typeof val === 'string' ? val.split(',') : val, module_ids: [] });
                            }}
                            input={<OutlinedInput label="Filières" />}
                            renderValue={(selected) => selected.map(id => filieres.find(f => String(f.id) === String(id))?.nom).filter(Boolean).join(', ')}
                            MenuProps={{ autoFocus: false, PaperProps: { style: { maxHeight: 400 } } }}
                            onClose={() => setFiliereSearch('')}
                        >
                            <ListSubheader sx={{ p: 1, bgcolor: 'background.paper' }}>
                                <TextField
                                    size="small"
                                    autoFocus
                                    fullWidth
                                    placeholder="Rechercher une filière..."
                                    value={filiereSearch}
                                    onChange={(e) => setFiliereSearch(e.target.value)}
                                    onKeyDown={(e) => { if (e.key !== 'Escape') e.stopPropagation(); }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </ListSubheader>
                            {filteredFilieres.map(f => (
                                <MenuItem key={f.id} value={f.id}>
                                    <Checkbox checked={form.filiere_ids.some(id => String(id) === String(f.id))} />
                                    <ListItemText primary={`${f.nom} (${f.code})`} />
                                </MenuItem>
                            ))}
                            {filteredFilieres.length === 0 && (
                                <MenuItem disabled>Aucune filière trouvée</MenuItem>
                            )}
                        </Select>
                    </FormControl>

                    {form.filiere_ids.length > 0 && (
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Modules</InputLabel>
                            <Select
                                multiple value={form.module_ids}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setForm({ ...form, module_ids: typeof val === 'string' ? val.split(',') : val });
                                }}
                                input={<OutlinedInput label="Modules" />}
                                renderValue={(selected) => {
                                    const names = filiereModules.filter(m => selected.some(id => String(id) === String(m.id))).map(m => m.name);
                                    return names.join(', ');
                                }}
                            >
                                {filiereModules.map(m => (
                                    <MenuItem key={m.id} value={m.id}>
                                        <Checkbox checked={form.module_ids.some(id => String(id) === String(m.id))} />
                                        <ListItemText primary={`${m.name} (${m.code}) — ${m.masse_horaire}h`} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {form.module_ids.length > 0 && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Masse horaire totale : <strong>{totalHours}h</strong> ({form.module_ids.length} modules)
                        </Alert>
                    )}

                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Niveaux requis</InputLabel>
                        <Select
                            multiple
                            value={form.required_levels}
                            onChange={(e) => setForm({ ...form, required_levels: e.target.value })}
                            input={<OutlinedInput label="Niveaux requis" />}
                            renderValue={(selected) => selected.join(', ')}
                        >
                            {LEVELS.map(l => (
                                <MenuItem key={l} value={l}>
                                    <Checkbox checked={form.required_levels.includes(l)} />
                                    <ListItemText primary={l} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Types de l'offre</InputLabel>
                        <Select
                            multiple
                            value={form.types}
                            onChange={(e) => setForm({ ...form, types: e.target.value })}
                            input={<OutlinedInput label="Types de l'offre" />}
                            renderValue={(selected) => selected.join(', ')}
                        >
                            {['CDJ', 'CDS', 'FQ'].map(t => (
                                <MenuItem key={t} value={t}>
                                    <Checkbox checked={form.types.includes(t)} />
                                    <ListItemText primary={t} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField fullWidth multiline rows={3} label="Description"
                        value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialog(false)} sx={{ textTransform: 'none' }}>Annuler</Button>
                    <Button variant="contained" onClick={handleSave}
                        sx={{ textTransform: 'none', background: 'linear-gradient(135deg, #1a5276, #2e86c1)' }}>
                        {editing ? 'Modifier' : 'Créer (brouillon)'}
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog open={!!deleteId} title="Supprimer l'offre"
                message="Cette action supprimera aussi toutes les candidatures liées. Continuer ?"
                onConfirm={handleDelete} onCancel={() => setDeleteId(null)} />
        </Box>
    );
}
